import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { BRAND, FAQS } from "@/lib/site";
import { TESTIDS } from "@/constants/testIds/site";
import SEO, { breadcrumbsLd, faqLd, localBusinessLd } from "@/components/site/SEO";
import AvailabilitySection from "@/components/site/AvailabilitySection";
import WhySection from "@/components/site/WhySection";
import FeaturedSpaces from "@/components/site/FeaturedSpaces";
import AmenitiesGrid from "@/components/site/AmenitiesGrid";
import Gallery from "@/components/site/Gallery";
import SocialProof from "@/components/site/SocialProof";
import FAQSection from "@/components/site/FAQSection";
import FinalCTA from "@/components/site/FinalCTA";

const HERO_IMG =
  "https://images.pexels.com/photos/19893586/pexels-photo-19893586.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2000";

const HomePage = () => {
  return (
    <>
      <SEO
        title="Office Space for Lease in Weston, FL"
        description="Furnished private offices, executive suites and team workspaces for lease in Weston, Florida. Move-in ready, transparently priced, minutes from I-75. Schedule a tour today."
        path="/"
        jsonLd={[
          localBusinessLd(BRAND),
          breadcrumbsLd([{ name: "Home", path: "/" }]),
          faqLd(FAQS),
        ]}
      />
      {/* HERO */}
      <section className="relative bg-[var(--bg)] overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 pt-12 md:pt-16 pb-0">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-end">
            <div className="md:col-span-7 fade-up">
              <div className="flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-[var(--text-muted)]">
                <span className="kbd-rule" />
                <MapPin size={13} strokeWidth={1.5} />
                Weston, Florida · est. 2014
              </div>

              <h1
                data-testid={TESTIDS.heroTitle}
                className="hero-display text-[56px] sm:text-[78px] lg:text-[104px] mt-8"
              >
                Professional office
                <br />
                <em className="italic text-[var(--primary)]">space, in Weston.</em>
              </h1>

              <p className="mt-8 max-w-xl text-[17px] leading-relaxed text-[var(--text-muted)]">
                Furnished private offices, executive suites and team
                workspaces in a serviced building minutes from I-75.
                Move-in ready, transparently priced, and built for the way
                small businesses actually work.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/contact"
                  data-testid={TESTIDS.heroPrimaryCta}
                  className="btn-primary justify-center"
                >
                  Schedule a Tour
                  <ArrowRight size={14} strokeWidth={1.7} />
                </Link>
                <Link
                  to="/available-spaces"
                  data-testid={TESTIDS.heroSecondaryCta}
                  className="btn-ghost justify-center"
                >
                  View available spaces
                </Link>
              </div>

              <div className="mt-10 flex items-center gap-4 text-[13px] text-[var(--text-muted)]">
                <a
                  href={`tel:${BRAND.phoneRaw}`}
                  className="inline-flex items-center gap-2 hover:text-[var(--primary)]"
                >
                  <Phone size={13} strokeWidth={1.5} />
                  Or call leasing at {BRAND.phone}
                </a>
              </div>
            </div>

            <div className="md:col-span-5 fade-up">
              <div className="hairline aspect-[4/5] overflow-hidden bg-[var(--border)] relative">
                <img
                  src={HERO_IMG}
                  alt="Weston Commercial Center lobby"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent" />
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                <div className="hairline bg-white py-4">
                  <div className="font-serif text-[24px] text-[var(--primary)] leading-none">120+</div>
                  <div className="text-[10px] mt-2 tracking-[0.16em] uppercase text-[var(--text-muted)]">Tenants</div>
                </div>
                <div className="hairline bg-white py-4">
                  <div className="font-serif text-[24px] text-[var(--primary)] leading-none">94%</div>
                  <div className="text-[10px] mt-2 tracking-[0.16em] uppercase text-[var(--text-muted)]">Retained</div>
                </div>
                <div className="hairline bg-white py-4">
                  <div className="font-serif text-[24px] text-[var(--primary)] leading-none">48h</div>
                  <div className="text-[10px] mt-2 tracking-[0.16em] uppercase text-[var(--text-muted)]">Move-in</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-24" />
      </section>

      <AvailabilitySection />
      <WhySection />
      <FeaturedSpaces />
      <AmenitiesGrid />
      <Gallery />
      <SocialProof />
      <FAQSection />
      <FinalCTA />
    </>
  );
};

export default HomePage;
